import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import debounce from 'lodash/debounce';
import AddIcon from '@material-ui/icons/Add';
import { RMSAutocomplete } from '../../../shared/components/form-controls/index';
import { RMSIconButton } from '../../components/buttons/index';
import { getFullPermissions } from 'reducers/PermissionsReducer';
import { executeApiAction } from '../../utility/apiHelper';
import generalServices, { getVehicleById } from '../../../shared/services/generalServices';

const SearchVehicleAutoComplete = ({
  name,
  label,
  onSelectionChange,
  placeholder,
  defaultVehicleId = null,
  defaultVehicleSelectedCallback = null,
  disabled = false,
  showAddIcon = false,
  onAddIconClick = () => {},
}) => {
  const isMounted = useRef(true);
  // Matches legacy's RMSVehicleLookup/index.js exactly — the inline Add-vehicle icon is
  // hidden (not disabled) unless the user has Vehicles.Create.
  const canCreateVehicle = getFullPermissions('globals', 'Vehicles', 'any').Create;
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);

  const searchVehicles = useCallback(async (searchText) => {
    await executeApiAction({
      setLoading,
      action: () => generalServices.getVehiclesBySearch(searchText),
      onSuccess: (response) => setVehicles(response),
    });
  }, []);

  const debouncedSearch = useMemo(
    () =>
      debounce((searchText) => {
        if (!searchText || searchText.trim().length < 3) {
          setVehicles([]);
          return;
        }

        searchVehicles(searchText);
      }, 500),
    [searchVehicles]
  );

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleDefaultVehicleId = useCallback(
    async (vehicleId) => {
      if (!vehicleId) return;

      await executeApiAction({
        setLoading,
        action: () => getVehicleById(vehicleId),
        onSuccess: (response) => {
          setVehicles((prev) => {
            const exists = prev.some((vehicle) => vehicle.ptsVehicleID === response.ptsVehicleID);
            return exists ? prev : [response, ...prev];
          });

          defaultVehicleSelectedCallback?.(response);
        },
      });
    },
    [defaultVehicleSelectedCallback]
  );

  useEffect(() => {
    handleDefaultVehicleId(defaultVehicleId);
  }, [defaultVehicleId, handleDefaultVehicleId]);

  return (
    <RMSAutocomplete
      name={name}
      label={label ?? 'Vehicle'}
      placeholder={placeholder}
      getOptionLabel={(option) =>
        [option.Make, option.Model, option.Year, option.PlateNumber].filter(Boolean).join(' - ')
      }
      optionLabel="PlateNumber"
      optionValue="ptsVehicleID"
      options={vehicles}
      onSelectionChange={(data) => onSelectionChange && onSelectionChange(data)}
      onSearch={debouncedSearch}
      disabled={disabled}
      loading={loading}
      leftContent={() =>
        showAddIcon &&
        canCreateVehicle && (
          <RMSIconButton
            icon={<AddIcon fontSize="small" />}
            tooltip="Add Vehicle"
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              onAddIconClick?.(event);
            }}
          />
        )
      }
    />
  );
};

export default SearchVehicleAutoComplete;
